"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { createClient } from "@/lib/supabase/client"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Checkbox } from "@/components/ui/checkbox"
import { Calendar, Clock, Users, Utensils, PartyPopper, MapPin, Sparkles, X, Loader2 } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { sendPartyInviteEmail } from "@/app/actions/send-email"
import { createNotificationWithPush } from "@/lib/utils/push-notifications"
import { usePlatform, triggerHaptic } from "@/hooks/use-platform"

interface CreatePartyModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  spotId?: string
  onPartyCreated: () => void
}

interface Friend {
  id: string
  name: string
  friend_id: string
}

export function CreatePartyModal({ open, onOpenChange, spotId, onPartyCreated }: CreatePartyModalProps) {
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [partyDate, setPartyDate] = useState("")
  const [timeType, setTimeType] = useState<"specific" | "part_of_day">("part_of_day")
  const [specificTime, setSpecificTime] = useState("")
  const [timeOfDay, setTimeOfDay] = useState("morning")
  const [activityType, setActivityType] = useState<"surf" | "kite">("surf")
  const [isPublic, setIsPublic] = useState(true)
  const [hasLunch, setHasLunch] = useState(false)
  const [hasDinner, setHasDinner] = useState(false)
  const [hasParty, setHasParty] = useState(false)
  const [selectedFriends, setSelectedFriends] = useState<string[]>([])
  const [friends, setFriends] = useState<Friend[]>([])
  const [nonFriendEmails, setNonFriendEmails] = useState<string[]>([])
  const [emailInput, setEmailInput] = useState("")
  const [loading, setLoading] = useState(false)
  const [spots, setSpots] = useState<{ id: string; name: string; country: string }[]>([])
  const [selectedSpotId, setSelectedSpotId] = useState<string>(spotId || "")

  useEffect(() => {
    if (open && !isPublic) {
      fetchFriends()
    }
  }, [open, isPublic])

  useEffect(() => {
    if (open && !spotId) {
      fetchSpots()
    }
  }, [open, spotId])

  const fetchSpots = async () => {
    const supabase = createClient()
    const { data, error } = await supabase
      .from("surf_spots")
      .select("id, name, country")
      .order("name", { ascending: true })

    if (!error && data) {
      setSpots(data as any)
    }
  }

  const fetchFriends = async () => {
    const supabase = createClient()
    const {
      data: { user },
    } = await supabase.auth.getUser()

    if (!user) return

    try {
      // First get the user profile ID
      const { data: userData } = await supabase
        .from("users")
        .select("id")
        .eq("auth_id", user.id)
        .single()

      if (!userData) return

      // Fetch friends - friends table uses auth_id, but we need users.id for invites
      const { data: friendsData, error } = await supabase
        .from("friends")
        .select("id, friend_id")
        .eq("user_id", user.id) // friends.user_id references users.auth_id
        .eq("status", "accepted")

      if (error) throw error

      // Convert friend auth_ids to user profile IDs and fetch friend details
      const friendAuthIds = friendsData?.map((f: any) => f.friend_id) || []
      
      if (friendAuthIds.length > 0) {
        const { data: friendProfiles } = await supabase
          .from("users")
          .select("id, name, first_name, last_name, email, auth_id")
          .in("auth_id", friendAuthIds)

        // Map to friends list with profile IDs
        const friendsList =
          friendsData
            ?.map((f: any) => {
              const profile = friendProfiles?.find((p: any) => p.auth_id === f.friend_id)
              if (!profile) return null
              
              // Get display name: prefer first_name + last_name, fallback to name, then email
              const displayName = 
                (profile.first_name && profile.last_name)
                  ? `${profile.first_name} ${profile.last_name}`
                  : profile.name || profile.email?.split("@")[0] || "Unknown"
              
              return {
                id: f.id,
                friend_id: profile.id, // Use users.id (profile ID) for invites
                name: displayName,
              }
            })
            .filter((f: any) => f !== null) || []

        setFriends(friendsList)
      } else {
        setFriends([])
      }
    } catch (error) {
      console.error("Error fetching friends:", error)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    triggerHaptic('selection')
    setLoading(true)

    const supabase = createClient()
    const {
      data: { user },
    } = await supabase.auth.getUser()

    if (!user) {
      triggerHaptic('error')
      alert("Please sign in to create a surf party")
      setLoading(false)
      return
    }

    // Validate private party has at least one invite
    if (!isPublic && selectedFriends.length === 0 && nonFriendEmails.length === 0) {
      triggerHaptic('error')
      alert("Please invite at least one person for a private party, or make it public.")
      setLoading(false)
      return
    }

    const effectiveSpotId = spotId || selectedSpotId

    if (!effectiveSpotId) {
      triggerHaptic('error')
      alert("Please select a surf spot")
      setLoading(false)
      return
    }

    try {
      const { data: userData, error: userError } = await supabase
        .from("users")
        .select("id, name")
        .eq("auth_id", user.id)
        .single()

      if (userError || !userData) {
        triggerHaptic('error')
        console.error("Error fetching user:", userError)
        alert("Could not find your user profile. Please try again.")
        setLoading(false)
        return
      }

      console.log("Creating party with user ID:", userData.id)

      const { data: spotData } = await supabase.from("surf_spots").select("name").eq("id", effectiveSpotId).single()

      const { data: partyData, error: partyError } = await supabase
        .from("surf_parties")
        .insert({
          surf_spot_id: effectiveSpotId,
          organizer_id: userData.id,
          title,
          description,
          party_date: partyDate,
          start_time: timeType === "specific" ? specificTime : null,
          time_of_day: timeType === "part_of_day" ? timeOfDay : null,
          activity_type: activityType,
          is_public: isPublic,
          has_lunch: hasLunch,
          has_dinner: hasDinner,
          has_party: hasParty,
        })
        .select()
        .single()

      if (partyError) {
        console.error("Party creation error:", partyError)
        throw partyError
      }

      console.log("Party created successfully:", partyData)

      const { error: participantError } = await supabase.from("surf_party_participants").insert({
        party_id: partyData.id,
        user_id: userData.id,
      })

      if (participantError) {
        console.error("Participant error:", participantError)
        throw participantError
      }

      // Handle invitations for private parties
      if (!isPublic && (selectedFriends.length > 0 || nonFriendEmails.length > 0)) {
        const timeDisplay =
          timeType === "specific" ? specificTime : timeOfDay.charAt(0).toUpperCase() + timeOfDay.slice(1)

        // Invite friends (existing users)
        if (selectedFriends.length > 0) {
          const friendInvites = selectedFriends.map((friendId) => ({
            party_id: partyData.id,
            inviter_id: userData.id,
            invitee_id: friendId,
            invitee_email: null, // Friends are identified by user ID
          }))

          const { error: inviteError } = await supabase.from("surf_party_invites").insert(friendInvites)

          if (inviteError) {
            console.error("Invite error:", inviteError)
            throw inviteError
          }

          // Send email invites to friends
          for (const friendId of selectedFriends) {
            const { data: friendData } = await supabase
              .from("users")
              .select("name, email")
              .eq("id", friendId)
              .single()

            if (friendData) {
              await createNotificationWithPush(supabase, {
                user_id: friendId,
                type: "party_invite",
                title: "Surf Party Invitation",
                message: `${userData.name || "A friend"} invited you to ${title}!`,
                action_url: `/spot/${effectiveSpotId}`,
                action_label: "View Party",
                related_id: partyData.id
              })

              await sendPartyInviteEmail(
                friendData.email,
                friendData.name || friendData.email.split("@")[0],
                userData.name || user.email?.split("@")[0] || "A surfer",
                title,
                partyDate,
                timeDisplay,
                activityType,
                spotData?.name || "Unknown Spot",
                partyData.id,
                hasLunch,
                hasDinner,
                hasParty,
              )
            }
          }
        }

        // Invite non-friends by email
        if (nonFriendEmails.length > 0) {
          const emailInvites = nonFriendEmails.map((email) => ({
            party_id: partyData.id,
            inviter_id: userData.id,
            invitee_id: null, // Non-friends don't have user ID yet
            invitee_email: email.toLowerCase().trim(),
          }))

          const { error: emailInviteError } = await supabase.from("surf_party_invites").insert(emailInvites)

          if (emailInviteError) {
            console.error("Email invite error:", emailInviteError)
            throw emailInviteError
          }

          // Send email invites to non-friends
          for (const email of nonFriendEmails) {
            await sendPartyInviteEmail(
              email.toLowerCase().trim(),
              email.split("@")[0], // Use email prefix as name
              userData.name || user.email?.split("@")[0] || "A surfer",
              title,
              partyDate,
              timeDisplay,
              activityType,
              spotData?.name || "Unknown Spot",
              partyData.id,
              hasLunch,
              hasDinner,
              hasParty,
            )
          }
        }
      }

      setTitle("")
      setDescription("")
      setPartyDate("")
      setSpecificTime("")
      setTimeOfDay("morning")
      setActivityType("surf")
      setIsPublic(true)
      setHasLunch(false)
      setHasDinner(false)
      setHasParty(false)
      setSelectedFriends([])
      setNonFriendEmails([])
      setEmailInput("")
      if (!spotId) setSelectedSpotId("")

      // Notify users within radius (for public parties only) and friends
      if (spotData) {
        try {
          // Get access token for authentication
          const { data: { session } } = await supabase.auth.getSession()
          const accessToken = session?.access_token

          fetch("/api/notifications/notify-activity", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(accessToken ? { "Authorization": `Bearer ${accessToken}` } : {})
            },
            body: JSON.stringify({
              type: 'party',
              spotId: effectiveSpotId,
              activityType: activityType,
              relatedId: partyData.id,
              partyDate: partyDate,
              isPublic: isPublic
            }),
          }).catch((error) => {
            console.error("Error notifying users:", error)
          })
        } catch (error) {
          console.error("Error preparing notifications:", error)
        }
      }

      triggerHaptic('success')
      onPartyCreated()
      onOpenChange(false)
    } catch (error) {
      triggerHaptic('error')
      console.error("Error creating party:", error)
      alert("Failed to create surf party. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleToggleFriend = (friendId: string) => {
    triggerHaptic('impact')
    setSelectedFriends((prev) => (prev.includes(friendId) ? prev.filter((id) => id !== friendId) : [...prev, friendId]))
  }

  const handleAddEmail = () => {
    const email = emailInput.trim().toLowerCase()
    if (email && email.includes("@") && !nonFriendEmails.includes(email)) {
      triggerHaptic('impact')
      setNonFriendEmails((prev) => [...prev, email])
      setEmailInput("")
    }
  }

  const handleRemoveEmail = (email: string) => {
    triggerHaptic('impact')
    setNonFriendEmails((prev) => prev.filter((e) => e !== email))
  }

  const handleEmailKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleAddEmail()
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[95vh] overflow-y-auto bg-white dark:bg-slate-900 border-none rounded-[2.5rem] shadow-2xl p-0 gap-0 z-[200] !top-24 sm:!top-[50%] !translate-y-0 sm:!translate-y-[-50%]">
        <div className="p-6 md:p-8 space-y-8 relative">
          <div className="flex items-start justify-between">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-blue-50 dark:bg-blue-900/20 rounded-2xl shadow-sm border border-blue-100/50 dark:border-blue-900/30">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <DialogTitle className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight">
                  Organize Surf Party
                </DialogTitle>
              </div>
              <DialogDescription className="text-[11px] font-medium text-slate-500 dark:text-slate-400 mt-1">
                Connect with local riders and plan your session
              </DialogDescription>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {!spotId && (
                <div className="space-y-2 col-span-full">
                  <Label htmlFor="spot" className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Select Surf Spot *</Label>
                  <Select value={selectedSpotId} onValueChange={setSelectedSpotId} required>
                    <SelectTrigger id="spot" className="h-12 rounded-2xl border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 !opacity-100">
                      <SelectValue placeholder="Choose a spot..." />
                    </SelectTrigger>
                    <SelectContent className="rounded-2xl border-slate-100 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900 z-[300]">
                      {spots.map((spot) => (
                        <SelectItem key={spot.id} value={spot.id} className="rounded-xl focus:bg-slate-50 dark:focus:bg-slate-800 !bg-white dark:!bg-slate-900">
                          {spot.name}, {spot.country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2 col-span-full">
                <Label htmlFor="title" className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Party Title *</Label>
                <Input
                  id="title"
                  placeholder="e.g., Morning Surf Session"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="h-12 rounded-2xl border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 focus:ring-blue-500/20 shadow-sm"
                  required
                />
              </div>

              <div className="space-y-2 col-span-full">
                <Label htmlFor="description" className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Add details about your surf party..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="rounded-2xl border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 focus:ring-blue-500/20 min-h-[100px] py-4 shadow-sm"
                  rows={3}
                />
              </div>

              <div className="space-y-3 col-span-full md:col-span-1">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Activity Type *</Label>
                <RadioGroup value={activityType} onValueChange={(v: any) => setActivityType(v)}>
                  <div className="grid grid-cols-2 gap-3">
                    <div
                      className={`flex items-center space-x-3 p-4 border rounded-2xl cursor-pointer transition-all active:scale-[0.98] ${
                        activityType === "surf"
                          ? "border-blue-500 bg-blue-50/50 dark:bg-blue-900/20"
                          : "border-slate-100 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-950/30 hover:bg-slate-50 dark:hover:bg-slate-900"
                      }`}
                      onClick={() => setActivityType("surf")}
                    >
                      <RadioGroupItem value="surf" id="surf" className="sr-only" />
                      <Label htmlFor="surf" className="font-bold cursor-pointer flex flex-col items-center gap-1 flex-1">
                        <span className="text-2xl">🏄</span>
                        <span className="text-[10px] uppercase tracking-wider">Surfing</span>
                      </Label>
                    </div>
                    <div
                      className={`flex items-center space-x-3 p-4 border rounded-2xl cursor-pointer transition-all active:scale-[0.98] ${
                        activityType === "kite"
                          ? "border-blue-500 bg-blue-50/50 dark:bg-blue-900/20"
                          : "border-slate-100 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-950/30 hover:bg-slate-50 dark:hover:bg-slate-900"
                      }`}
                      onClick={() => setActivityType("kite")}
                    >
                      <RadioGroupItem value="kite" id="kite" className="sr-only" />
                      <Label htmlFor="kite" className="font-bold cursor-pointer flex flex-col items-center gap-1 flex-1">
                        <span className="text-2xl">🪁</span>
                        <span className="text-[10px] uppercase tracking-wider">Kiting</span>
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-2 col-span-full md:col-span-1">
                <Label htmlFor="date" className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1 flex items-center gap-2">
                  <Calendar className="w-3 h-3" />
                  Date *
                </Label>
                <Input
                  id="date"
                  type="date"
                  value={partyDate}
                  onChange={(e) => setPartyDate(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                  className="h-12 rounded-2xl border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/50"
                  required
                />
              </div>

              <div className="space-y-4 col-span-full">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1 flex items-center gap-2">
                  <Clock className="w-3 h-3" />
                  Time Preference
                </Label>
                <div className="bg-slate-50 dark:bg-slate-950 rounded-[2rem] p-6 border border-slate-100 dark:border-slate-800 space-y-6 shadow-sm">
                  <RadioGroup value={timeType} onValueChange={(v: any) => setTimeType(v)} className="flex gap-6">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="part_of_day" id="part_of_day" className="border-blue-500" />
                      <Label htmlFor="part_of_day" className="text-[11px] font-bold uppercase tracking-wider cursor-pointer">
                        Part of day
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="specific" id="specific" className="border-blue-500" />
                      <Label htmlFor="specific" className="text-[11px] font-bold uppercase tracking-wider cursor-pointer">
                        Specific time
                      </Label>
                    </div>
                  </RadioGroup>

                  {timeType === "part_of_day" ? (
                    <RadioGroup value={timeOfDay} onValueChange={setTimeOfDay}>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {['morning', 'afternoon', 'evening', 'all_day'].map((time) => (
                          <div
                            key={time}
                            className={`flex items-center justify-center p-3 border rounded-xl cursor-pointer transition-all ${
                              timeOfDay === time
                                ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-600"
                                : "border-slate-100 dark:border-slate-800 text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-900"
                            }`}
                            onClick={() => setTimeOfDay(time)}
                          >
                            <RadioGroupItem value={time} id={time} className="sr-only" />
                            <Label htmlFor={time} className="text-[9px] font-black uppercase tracking-widest cursor-pointer">
                              {time.replace('_', ' ')}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </RadioGroup>
                  ) : (
                    <Input 
                      type="time" 
                      value={specificTime} 
                      onChange={(e) => setSpecificTime(e.target.value)} 
                      className="h-12 rounded-2xl border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900"
                    />
                  )}
                </div>
              </div>

              <div className="space-y-4 col-span-full">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Additional Activities</Label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { id: 'lunch', label: 'Lunch', icon: Utensils, state: hasLunch, setter: setHasLunch },
                    { id: 'dinner', label: 'Dinner', icon: Utensils, state: hasDinner, setter: setHasDinner },
                    { id: 'party', label: 'Party', icon: PartyPopper, state: hasParty, setter: setHasParty }
                  ].map((act) => (
                    <div
                      key={act.id}
                      onClick={() => act.setter(!act.state)}
                      className={`flex flex-col items-center gap-2 p-4 border rounded-2xl cursor-pointer transition-all active:scale-[0.95] ${
                        act.state 
                          ? "border-orange-500 bg-orange-50/50 dark:bg-orange-900/20 text-orange-600" 
                          : "border-slate-100 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-950/30 text-slate-400"
                      }`}
                    >
                      <act.icon className={`w-5 h-5 ${act.state ? "text-orange-500" : "text-slate-300"}`} />
                      <span className="text-[9px] font-black uppercase tracking-widest">{act.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="col-span-full bg-slate-50 dark:bg-slate-950 rounded-[2rem] p-6 border border-slate-100 dark:border-slate-800 shadow-sm">
                <div className="flex items-center justify-between gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="public" className="font-bold text-slate-900 dark:text-white flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-blue-500" />
                      Party Visibility
                    </Label>
                    <p className="text-[10px] font-medium text-slate-500">
                      {isPublic 
                        ? "Anyone can see and join this party" 
                        : "Only invited people can see and join this party"}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                      {isPublic ? "Public" : "Private"}
                    </span>
                    <Switch 
                      id="public" 
                      checked={isPublic} 
                      onCheckedChange={setIsPublic}
                      className="data-[state=checked]:bg-blue-600"
                    />
                  </div>
                </div>

                {!isPublic && (
                  <div className="mt-8 space-y-6 animate-in fade-in slide-in-from-top-2 duration-300">
                    {/* Invite Friends Section */}
                    <div className="space-y-3">
                      <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Invite Friends</Label>
                      {friends.length === 0 ? (
                        <div className="p-6 bg-white dark:bg-slate-900 rounded-2xl border-dashed border border-slate-200 dark:border-slate-800 text-center">
                          <p className="text-[11px] text-slate-400 italic">No friends to invite yet. Add some buddies first!</p>
                        </div>
                      ) : (
                        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden divide-y divide-slate-50 dark:divide-slate-800/50">
                          {friends.map((friend) => (
                            <div 
                              key={friend.id} 
                              className="flex items-center gap-3 p-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer"
                              onClick={() => handleToggleFriend(friend.friend_id)}
                            >
                              <Checkbox
                                id={friend.id}
                                checked={selectedFriends.includes(friend.friend_id)}
                                onCheckedChange={() => handleToggleFriend(friend.friend_id)}
                                className="rounded-lg border-slate-200 data-[state=checked]:bg-blue-600"
                              />
                              <Label htmlFor={friend.id} className="font-bold text-sm cursor-pointer flex-1 py-1">
                                {friend.name}
                              </Label>
                            </div>
                          ))}
                        </div>
                      )}
                      {selectedFriends.length > 0 && (
                        <Badge className="bg-blue-50 text-blue-600 border-none rounded-lg text-[9px] font-black uppercase px-2 py-1">
                          {selectedFriends.length} friend{selectedFriends.length > 1 ? "s" : ""} selected
                        </Badge>
                      )}
                    </div>

                    {/* Invite Non-Friends by Email */}
                    <div className="space-y-3">
                      <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Invite by Email</Label>
                      <div className="flex gap-2">
                        <Input
                          type="email"
                          placeholder="buddy@surf.com"
                          value={emailInput}
                          onChange={(e) => setEmailInput(e.target.value)}
                          onKeyPress={handleEmailKeyPress}
                          className="h-11 rounded-xl bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800"
                        />
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={handleAddEmail}
                          disabled={!emailInput.trim() || !emailInput.includes("@")}
                          className="h-11 px-4 rounded-xl font-bold text-xs"
                        >
                          Add
                        </Button>
                      </div>
                      {nonFriendEmails.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {nonFriendEmails.map((email) => (
                            <Badge key={email} className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-none rounded-lg text-[9px] font-bold py-1 pl-2 pr-1 flex items-center gap-1">
                              {email}
                              <button
                                type="button"
                                onClick={() => handleRemoveEmail(email)}
                                className="p-0.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-md transition-colors"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>

                    {(selectedFriends.length === 0 && nonFriendEmails.length === 0) && (
                      <div className="p-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/30 rounded-2xl flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center shrink-0">
                          <Sparkles className="w-4 h-4 text-amber-600" />
                        </div>
                        <p className="text-[10px] font-bold text-amber-700 dark:text-amber-400 leading-tight">
                          Private party: Please invite at least one person to get started.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="pt-4 flex flex-col sm:flex-row gap-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => onOpenChange(false)} 
                disabled={loading}
                className="h-14 flex-1 rounded-[1.25rem] font-bold text-slate-500 border-slate-100 dark:border-slate-800"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading} 
                className="h-14 flex-[2] rounded-[1.25rem] bg-blue-600 hover:bg-blue-700 text-white font-black uppercase tracking-widest text-xs shadow-xl shadow-blue-500/25 active:scale-95 transition-all"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Organize Party"}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  )
}
